import { useEffect, useRef, useState } from 'react';

import './SupportWidgetController.scss';
import { SUPPORT_WIDGET_APP_URL, SUPPORT_WIDGET_SRC } from 'shared/settings/config';

const SUPPORT_WIDGET_SCRIPT_ID = 'ctbids-support-widget';
const SUPPORT_WIDGET_CONTAINER_ID = 'ct-bids-widget-container';
const SUPPORT_WIDGET_ROUTE = '/help-center';
const SUPPORT_WIDGET_SYSTEM_TYPE = 'seller';

const isSupportWidgetRoute = (pathname = '') => pathname.startsWith(SUPPORT_WIDGET_ROUTE);

const removeSupportWidgetElements = () => {
    try {
        window.CTBIDS_SUPPORT_WIDGET?.destroy?.();
    } catch (error) {
        // Widget cleanup should never block navigation or logout.
    }

    document.getElementById(SUPPORT_WIDGET_CONTAINER_ID)?.remove();
    document.getElementById(SUPPORT_WIDGET_SCRIPT_ID)?.remove();

    document
        .querySelectorAll(`iframe[src*="${SUPPORT_WIDGET_APP_URL}"]`)
        .forEach(element => element.remove());
};

export const SupportWidgetController = ({ pathname, userId }) => {
    const pathnameRef = useRef(pathname);
    const userIdRef = useRef(userId);
    const isChatOpenRef = useRef(false);
    const hasActiveChatRef = useRef(false);
    const [showBottomCloseControl, setShowBottomCloseControl] = useState(false);

    useEffect(() => {
        pathnameRef.current = pathname;
    }, [pathname]);

    useEffect(() => {
        const getWidgetContainer = () => document.getElementById(SUPPORT_WIDGET_CONTAINER_ID);

        const syncWidgetVisibility = () => {
            const container = getWidgetContainer();
            const isHelpChatRoute = isSupportWidgetRoute(pathnameRef.current);

            if (!container) {
                setShowBottomCloseControl(false);
                return;
            }

            const shouldShow = isHelpChatRoute || hasActiveChatRef.current;
            container.style.display = shouldShow ? '' : 'none';
            setShowBottomCloseControl(!isHelpChatRoute && shouldShow && isChatOpenRef.current);
        };

        const loadSupportWidget = () => {
            if (document.getElementById(SUPPORT_WIDGET_SCRIPT_ID)) {
                syncWidgetVisibility();
                return;
            }

            const script = document.createElement('script');
            script.id = SUPPORT_WIDGET_SCRIPT_ID;
            script.src = SUPPORT_WIDGET_SRC;
            script.async = true;
            script.setAttribute('data-app-url', SUPPORT_WIDGET_APP_URL);
            script.setAttribute('data-system-type', SUPPORT_WIDGET_SYSTEM_TYPE);

            document.body.appendChild(script);
        };

        const closeFloatingSupportWidget = () => {
            isChatOpenRef.current = false;
            hasActiveChatRef.current = false;
            setShowBottomCloseControl(false);
            removeSupportWidgetElements();
        };

        const handleWidgetMessage = (event) => {
            if (event.origin !== SUPPORT_WIDGET_APP_URL || event.data?.type !== 'ct-widget-toggle') {
                return;
            }

            isChatOpenRef.current = !!event.data.isOpen;

            if (isChatOpenRef.current && isSupportWidgetRoute(pathnameRef.current)) {
                hasActiveChatRef.current = true;
            }

            if (!isChatOpenRef.current && isSupportWidgetRoute(pathnameRef.current)) {
                hasActiveChatRef.current = false;
            }

            syncWidgetVisibility();
        };

        window.__closeCtbidsSupportWidget = closeFloatingSupportWidget;

        const observer = new MutationObserver(syncWidgetVisibility);

        window.addEventListener('message', handleWidgetMessage);
        observer.observe(document.body, { childList: true });

        if (isSupportWidgetRoute(pathnameRef.current)) {
            loadSupportWidget();
        } else {
            syncWidgetVisibility();
        }

        return () => {
            window.removeEventListener('message', handleWidgetMessage);
            observer.disconnect();
            delete window.__closeCtbidsSupportWidget;
            removeSupportWidgetElements();
        };
    }, []);

    useEffect(() => {
        const wasLoggedIn = !!userIdRef.current;
        const isLoggedIn = !!userId;

        userIdRef.current = userId;

        if (!wasLoggedIn || isLoggedIn) {
            return;
        }

        isChatOpenRef.current = false;
        hasActiveChatRef.current = false;
        setShowBottomCloseControl(false);
        removeSupportWidgetElements();
    }, [userId]);

    useEffect(() => {
        const container = document.getElementById(SUPPORT_WIDGET_CONTAINER_ID);
        const script = document.getElementById(SUPPORT_WIDGET_SCRIPT_ID);
        const isHelpChatRoute = isSupportWidgetRoute(pathname);

        if (isHelpChatRoute) {
            if (!script) {
                const supportWidgetScript = document.createElement('script');
                supportWidgetScript.id = SUPPORT_WIDGET_SCRIPT_ID;
                supportWidgetScript.src = SUPPORT_WIDGET_SRC;
                supportWidgetScript.async = true;
                supportWidgetScript.setAttribute('data-app-url', SUPPORT_WIDGET_APP_URL);
                supportWidgetScript.setAttribute('data-system-type', SUPPORT_WIDGET_SYSTEM_TYPE);

                document.body.appendChild(supportWidgetScript);
            }

            if (container) {
                container.style.display = '';
            }

            setShowBottomCloseControl(false);
            return;
        }

        if (container) {
            container.style.display = hasActiveChatRef.current ? '' : 'none';
        }

        setShowBottomCloseControl(!!container && hasActiveChatRef.current && isChatOpenRef.current);
    }, [pathname, userId]);

    const handleBottomClose = (event) => {
        event.preventDefault();
        event.stopPropagation();
        window.__closeCtbidsSupportWidget?.();
    };

    if (!showBottomCloseControl) {
        return null;
    }

    return (
        <div
            id="chatbot-close-id"
            className="ctbids-support-bottom-close"
            role="button"
            tabIndex={0}
            aria-label="Close support chat"
            onClick={handleBottomClose}
            onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    handleBottomClose(event);
                }
            }}
        />
    );
};
